"use client";

import React from "react";
import { Quote, GraduationCap, Award } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const teamMembers = [{
  name: "Dr. Samuel L. Sharmat",
  role: "Lead Psychiatrist",
  image: "https://vast-cheetah.10web.cloud/wp-content/uploads/2026/05/samuel-sharmat-color-1365.png?v=h9utcntds7u",
  bio: "Award-winning psychiatrist specializing in complex adult cases and genetic psychiatry. With over 20 years of experience, Dr. Sharmat leads the practice with a focus on precision medicine and deep empathetic engagement.",
  education: ["Doctor of Medicine (MD)", "Psychiatry Residency", "Addiction Psychiatry Fellowship"],
  awards: ["Award-Winning Psychiatrist", "Top Doctor Recognition", "Excellence in Care"],
  quote: "We bridge the gap between advanced genetic science and the human need for being truly heard. Every patient deserves a treatment plan as unique as their DNA.",
  link: "https://sharmatpsychiatricgroup.com/dr-sharmat-profile/"
}, {
  name: "Maryellen Stephens",
  role: "Board-certified Nurse Practitioner",
  image: "https://vast-cheetah.10web.cloud/wp-content/uploads/2026/02/Maryellen-pic-edit-crop-50pct-e1771889345483.png?v=flv8k8r4nj",
  bio: "Expert in medication management and patient-centered care, serving clients of all ages. Maryellen combines scientific rigor with a warm, approachable style that puts patients at ease immediately.",
  education: ["Master of Science in Nursing (MSN)", "Board Certified PMHNP", "Pediatric Mental Health Specialist"],
  awards: ["Board Certified", "Patient Advocacy Award", "Compassionate Care"],
  quote: "Healing begins when a patient feels understood, respected, and empowered. I believe in treating the whole person, not just the symptoms.",
  link: "https://sharmatpsychiatricgroup.com/maryellen-stephens-profile/"
}];
export default function TheCareTeam() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-care-team" className="w-full flex flex-col" ref={ref}>
      {teamMembers.map((member, index) => {
      const isEven = index % 2 === 0;
      const bgClass = isEven ? "bg-background" : "bg-secondary/30";
      const orderClass = isEven ? "" : "lg:flex-row-reverse";
      return <div key={member.name} id={member.name.toLowerCase().replace(/[\s.]/g, "-")} data-index={index} className={`w-full py-20 lg:py-32 ${bgClass} border-b border-border/40 last:border-0`}>
            <div className={`container mx-auto px-6 lg:px-12 flex flex-col lg:flex-row gap-12 lg:gap-20 items-center ${orderClass}`}>
              
              {}
              <div className={`w-full lg:w-[40%] relative aspect-[3/4] lg:aspect-[4/5] overflow-hidden rounded-xl shadow-xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${index * 150}ms`
          }}>
                <div className="absolute inset-0 bg-primary/10 mix-blend-multiply z-10 pointer-events-none" />
                <img src={member.image} alt={member.name} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700 ease-in-out scale-100 hover:scale-105" />
              </div>

              {}
              <div className={`w-full lg:w-[60%] flex flex-col gap-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`} style={{
            transitionDelay: `${index * 150 + 200}ms`
          }}>
                {}
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {member.awards.map((award, i) => <Badge key={i} variant="secondary" className="rounded-lg px-3 py-1 text-xs uppercase tracking-wider font-medium">
                        <Award className="w-3 h-3 mr-1.5 text-primary" />
                        {award}
                      </Badge>)}
                  </div>
                  <h2 className="text-4xl lg:text-5xl font-serif text-foreground tracking-tight">
                    {member.name}
                  </h2>
                  <p className="text-lg text-muted-foreground font-medium uppercase tracking-wide">
                    {member.role}
                  </p>
                </div>

                {}
                <p className="text-lg leading-relaxed text-muted-foreground/90 max-w-2xl">
                  {member.bio}
                </p>

                {}
                <blockquote className="relative pl-6 border-l-4 border-primary/20 italic text-xl text-foreground/80 font-serif my-2">
                  <Quote className="absolute -top-2 -left-2 w-6 h-6 text-primary/10 -z-10 fill-current" />
                  "{member.quote}"
                </blockquote>

                {}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" />
                    Education & Credentials
                  </h4>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                    {member.education.map((edu, i) => <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-lg bg-primary/40 shrink-0" />
                        {edu}
                      </li>)}
                  </ul>
                </div>

                {}
                <div className="pt-4">
                  <Button asChild variant="outline" className="rounded-lg px-8 border-primary/20 hover:bg-primary hover:text-primary-foreground transition-colors">
                    <Link to={member.link}>
                      View Full Profile
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>;
    })}
    </section>;
}